import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Globe,
  TrendingUp,
  TrendingDown,
  FileText,
  ArrowRight,
  Newspaper,
  Activity,
  Shield,
  Zap,
  Navigation,
  MapPin,
  Calculator,
  Radio,
  Gauge,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import StatCard from '../components/StatCard';
import { STITCH_IMAGES } from '../constants/stitchAssets';
import LegislationCard from '../components/LegislationCard';
import TrendingNewsCard from '../components/TrendingNewsCard';
import { statsAPI, legislationAPI, newsAPI, gtiAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const COLORS = ['#b4c5ff', '#2563eb', '#4edea3', '#ffb4ab', '#b9c7df', '#8d90a0', '#007d55', '#f59e0b'];

const Dashboard = () => {
  const [globalStats, setGlobalStats] = useState(null);
  const [recentLegislation, setRecentLegislation] = useState([]);
  const [topCountries, setTopCountries] = useState([]);
  const [trendingNews, setTrendingNews] = useState([]);
  const [gtiRankings, setGtiRankings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, legislationRes, countriesRes, newsRes] = await Promise.all([
        statsAPI.getGlobal(),
        legislationAPI.getAll({ limit: 6 }),
        statsAPI.getTopCountries({ limit: 5, order: 'desc' }),
        newsAPI.getTrending({ limit: 8 }),
      ]);

      setGlobalStats(statsRes.data);
      setRecentLegislation(legislationRes.data);
      setTopCountries(countriesRes.data);
      setTrendingNews(newsRes.data || []);

      // Also fetch GTI rankings (v2)
      try {
        const gtiRes = await gtiAPI.getRankings({ top: 10, bottom: 5 });
        setGtiRankings(gtiRes.data);
      } catch (e) {
        console.warn('GTI data not available yet');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-primary-container border-t-transparent dark:border-primary" />
          <p className="mt-4 text-slate-600 dark:text-on-surface-variant">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const intel = globalStats?.intelligence || {};

  // GTI chart data
  const gtiChartData = gtiRankings?.topCountries?.map((c, i) => ({
    name: c.country_code,
    'GTI Score': parseFloat(c.gti_score || 0).toFixed(1),
    events: c.event_count || 0,
  })) || [];

  // Top countries chart data (now from v2 GTI/trade_events data)
  const tariffChartData = topCountries.map(country => ({
    name: country.code,
    'GTI Score': parseFloat(country.avg_tariff || 0).toFixed(1),
    'Events': parseInt(country.tariff_count || 0),
  }));

  // Sector benchmark data
  const sectorData = (intel.sectors || []).map(s => ({
    name: s.sector,
    score: parseFloat(s.avg_gti_score || 0).toFixed(1),
    countries: s.country_count,
    events: s.event_count,
  }));

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <SEOHead
        title="Global Trade Intelligence Dashboard | Tariffs, Import Export & Sanctions"
        description="TradeRadar's AI-powered dashboard tracks global tariffs, import export bans, trade deals, sanctions, Iran conflict oil impact, Middle East trade disruption, and the Global Trade Index (GTI) across 190+ countries in real time."
        path="/"
        keywords="global trade dashboard, tariffs tracker, import export data, trade deals, sanctions, trade ban, global trade index, GTI, international trade intelligence, Iran conflict, oil price today, Middle East conflict 2026, Strait of Hormuz shipping, gold price today, Iran war updates"
      />

      {/* Stitch-style hero */}
      <section className="relative mb-10 overflow-hidden rounded-[2rem] border border-outline-variant/15 bg-gradient-to-br from-primary-container/30 via-surface-container-low to-surface-container p-8 shadow-intel-glow dark:from-primary-container/20 dark:via-surface-container-low dark:to-background md:p-10 lg:p-12">
        <svg
          className="pointer-events-none absolute -right-8 bottom-0 h-48 w-[min(100%,28rem)] text-primary/25 dark:text-primary/35 md:h-64 lg:right-0 lg:h-72"
          viewBox="0 0 400 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d="M0 96 C80 72 120 108 200 84 C280 60 320 96 400 72 L400 120 L0 120 Z"
            fill="currentColor"
            opacity="0.35"
          />
          <path
            d="M0 108 C100 80 140 100 220 88 C300 76 340 104 400 88 L400 120 L0 120 Z"
            fill="currentColor"
            opacity="0.2"
          />
        </svg>
        <div className="relative z-10 max-w-3xl">
          <p className="label-intel mb-2 text-primary-700 dark:text-primary">Intelligence Core v4.2</p>
          <h1 className="font-headline text-3xl font-bold tracking-tight text-slate-900 dark:text-on-surface md:text-4xl lg:text-5xl">
            AI-powered insights into global trade policies.
          </h1>
          <p className="mt-4 max-w-2xl text-slate-600 dark:text-on-surface-variant">
            Tariffs, sanctions, trade deals, and the Global Trade Index (GTI)—monitored across 190+ economies with analyst-grade context.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/tariffs"
              className="inline-flex items-center rounded-stitch-lg bg-gradient-to-r from-primary to-blue-700 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:opacity-95"
            >
              Explore tariffs
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <Link
              to="/compare"
              className="inline-flex items-center rounded-stitch-lg border border-outline-variant/30 bg-white/80 px-5 py-2.5 text-sm font-semibold text-slate-800 backdrop-blur-sm transition hover:bg-white dark:border-outline-variant/25 dark:bg-surface-container-high/80 dark:text-on-surface dark:hover:bg-surface-container-high"
            >
              Compare GTI
            </Link>
          </div>
        </div>
      </section>

      {/* Glass metrics */}
      <div className="mb-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          variant="glass"
          title="Tracked Countries"
          value={intel.gtiCountries || globalStats?.countries || 0}
          change={intel.gtiCountries ? `${globalStats?.countries || 0} in DB, ${intel.gtiCountries} with GTI` : undefined}
          icon={Globe}
          color="blue"
        />
        <StatCard
          variant="glass"
          title="AI Trade Events"
          value={intel.tradeEvents || 0}
          change={intel.tradeEvents > 0 ? `Avg impact: ${intel.avgImpact}` : 'Run pipeline to extract'}
          changeType={intel.tradeEvents > 0 ? 'increase' : undefined}
          icon={Zap}
          color="yellow"
        />
        <StatCard
          variant="glass"
          title="Trade Deals Detected"
          value={(intel.dealEvents || 0) + (globalStats?.tradeDeals?.active || 0)}
          change={intel.dealEvents > 0 ? `${intel.dealEvents} AI-extracted` : undefined}
          changeType={intel.dealEvents > 0 ? 'increase' : undefined}
          icon={FileText}
          color="green"
        />
        <StatCard
          variant="glass"
          title="Sanctions & Restrictions"
          value={(intel.sanctionEvents || 0) + (intel.restrictionEvents || 0)}
          change={`${intel.sanctionEvents || 0} sanctions, ${intel.restrictionEvents || 0} bans`}
          changeType={(intel.sanctionEvents || 0) > 0 ? 'decrease' : undefined}
          icon={Shield}
          color="red"
        />
      </div>

      {/* Middle East / Hormuz, real-time impact, then horizontal intelligence feed */}
      <div className="mb-10 space-y-6">
        <div className="overflow-hidden rounded-3xl bg-gradient-to-br from-red-700 via-red-600 to-orange-600 p-6 text-white shadow-intel-glow dark:from-error-container dark:via-red-700 dark:to-orange-700 md:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <div className="mb-3 flex items-center gap-3">
                <Activity className="h-7 w-7 shrink-0 opacity-95" />
                <Link
                  to="/middle-east-impact-dashboard"
                  className="font-headline text-xl font-bold leading-tight tracking-tight text-white transition hover:underline md:text-2xl lg:text-3xl"
                >
                  Iran Conflict Dashboard 2026 — Oil, Gold & Global Trade Impact
                </Link>
              </div>
              <p className="mb-6 max-w-3xl text-sm leading-relaxed text-red-50/95 md:text-base">
                Track real-time Iran conflict updates and their impact on oil prices, gold safe-haven demand, Strait of
                Hormuz shipping, stock markets, and global trade. Monitor Brent crude, WTI crude, gold prices, and
                maritime risk as Middle East tensions affect global energy markets.
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  {
                    label: 'Oil Price Today & Iran Conflict Oil Impact',
                    to: '/middle-east-impact-dashboard#oil',
                  },
                  {
                    label: 'Gold Safe Haven 2026 & Gold Price Today',
                    to: '/middle-east-impact-dashboard#gold',
                  },
                  {
                    label: 'Strait of Hormuz Shipping & Trade Routes',
                    to: '/middle-east-impact-dashboard#shipping',
                  },
                  {
                    label: 'Middle East Conflict 2026 Updates',
                    to: '/middle-east-impact-dashboard#markets',
                  },
                ].map(({ label, to }) => (
                  <Link
                    key={label}
                    to={to}
                    className="group rounded-2xl border border-white/25 bg-white/10 px-3 py-3 text-left text-xs font-semibold leading-snug backdrop-blur-md transition hover:border-white/40 hover:bg-white/20 md:text-[13px]"
                  >
                    <span className="line-clamp-3">{label}</span>
                    <span className="mt-2 flex items-center gap-1 text-[10px] font-normal text-white/85">
                      Open section
                      <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/middle-east-impact-dashboard"
              className="inline-flex shrink-0 items-center gap-2 self-end rounded-2xl border border-white/30 bg-white/15 px-4 py-2.5 text-sm font-semibold text-white backdrop-blur-md transition hover:bg-white/25 sm:self-start"
            >
              Full dashboard
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-3xl border border-orange-500/30 shadow-intel-glow ring-1 ring-white/10">
          <img
            src={STITCH_IMAGES.dashboardHormuzMap.src}
            alt={STITCH_IMAGES.dashboardHormuzMap.alt}
            className="h-[min(22rem,55vw)] min-h-56 w-full object-cover sm:min-h-64"
          />
          <div
            className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-900/55 to-amber-900/25 dark:from-background/95 dark:via-slate-950/70"
            aria-hidden
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-transparent to-orange-900/20" aria-hidden />
          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-7">
            <div className="mb-4 flex flex-wrap items-start gap-3">
              <MapPin className="mt-1 h-6 w-6 shrink-0 text-amber-200" />
              <div className="min-w-0 flex-1">
                <h2 className="font-headline text-xl font-bold text-white drop-shadow-sm md:text-2xl">
                  Strait of Hormuz Authority Hub
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-white/90 md:text-base">
                  Complete data-driven guide to the world&apos;s most critical oil chokepoint. Real-time risk scores,
                  shipping routes, economic impact analysis, and live updates.
                </p>
                <p className="mt-3 max-w-2xl text-xs leading-relaxed text-amber-100/90 md:text-sm">
                  {intel.tradeEvents > 0
                    ? `${intel.tradeEvents} AI trade events in the database — pair with Hormuz scenarios and risk tools below.`
                    : 'Run the pipeline to populate live trade events; the hub still covers risk scores, routes, and calculators.'}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/strait-of-hormuz/risk-dashboard"
                  className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/15 px-3 py-2 text-xs font-semibold text-white backdrop-blur-md transition hover:border-amber-300/50 hover:bg-white/25 md:text-sm"
                >
                  <Gauge className="h-4 w-4 shrink-0 text-amber-200" />
                  Live Risk Dashboard
                </Link>
                <Link
                  to="/strait-of-hormuz/oil-impact-calculator"
                  className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/15 px-3 py-2 text-xs font-semibold text-white backdrop-blur-md transition hover:border-amber-300/50 hover:bg-white/25 md:text-sm"
                >
                  <Calculator className="h-4 w-4 shrink-0 text-amber-200" />
                  Oil Impact Calculator
                </Link>
                <Link
                  to="/strait-of-hormuz/live-updates"
                  className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/15 px-3 py-2 text-xs font-semibold text-white backdrop-blur-md transition hover:border-amber-300/50 hover:bg-white/25 md:text-sm"
                >
                  <Radio className="h-4 w-4 shrink-0 text-amber-200" />
                  Real-Time Updates
                </Link>
              </div>
              <Link
                to="/strait-of-hormuz"
                className="inline-flex shrink-0 items-center justify-center rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 px-5 py-2.5 text-sm font-semibold text-slate-950 shadow-lg transition hover:opacity-95 dark:from-amber-400 dark:to-orange-500 dark:text-slate-950"
              >
                Hormuz hub overview
                <Navigation className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        <section
          className="rounded-3xl border border-outline-variant/15 bg-surface-container p-5 dark:bg-surface-container-low"
          aria-label="Intelligence feed"
        >
          <div className="mb-4 flex items-center gap-2">
            <Newspaper className="h-5 w-5 text-primary dark:text-primary" />
            <h2 className="font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">Intelligence feed</h2>
          </div>
          {trendingNews.length > 0 ? (
            <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 pt-1 [-webkit-overflow-scrolling:touch]">
              {trendingNews.slice(0, 8).map((newsItem, index) => (
                <div
                  key={`${newsItem.type}-${newsItem.id}-${index}`}
                  className="min-w-[280px] max-w-[320px] shrink-0 snap-start"
                >
                  <TrendingNewsCard newsItem={newsItem} variant="strip" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 dark:text-on-surface-variant">
              No trending items yet. Check back after the news pipeline runs.
            </p>
          )}
        </section>
      </div>

      {/* ── GTI Rankings (v2) — decorative map backdrop ── */}
      {gtiRankings && gtiRankings.topCountries?.length > 0 && (
        <div className="relative mb-8 overflow-hidden rounded-[2rem] border border-outline-variant/15">
          <img
            src={STITCH_IMAGES.dashboardWorldMap.src}
            alt=""
            className="pointer-events-none absolute -right-4 top-0 h-full w-1/2 max-w-md object-cover opacity-[0.14] dark:opacity-[0.22]"
            aria-hidden
          />
          <div className="relative z-10 grid grid-cols-1 gap-6 bg-white/85 p-4 backdrop-blur-sm dark:bg-surface-container/90 sm:p-6 lg:grid-cols-2">
          {/* GTI Bar Chart */}
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 transition-colors dark:border-outline-variant/15 dark:bg-surface-container">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-headline text-xl font-semibold text-slate-900 dark:text-on-surface">🌐 Global Trade Index — Top Countries</h2>
              <Link
                to="/compare"
                className="flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary dark:hover:text-primary-fixed-dim"
              >
                Compare <ArrowRight className="h-3 w-3 ml-1" />
              </Link>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={gtiChartData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={50} />
                <Tooltip
                  formatter={(value, name) => [
                    name === 'GTI Score' ? `${value} / 100` : value,
                    name,
                  ]}
                />
                <Legend />
                <Bar dataKey="GTI Score" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                  {gtiChartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* GTI Ranking Table */}
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 transition-colors dark:border-outline-variant/15 dark:bg-surface-container">
            <h2 className="font-headline text-xl font-semibold text-slate-900 dark:text-on-surface mb-4">📊 GTI Country Rankings</h2>
            <div className="overflow-y-auto max-h-[340px]">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-slate-100 dark:bg-surface-container-low">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                      Rank
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                      Country
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                      GTI Score
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                      Events
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                      Trend
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {gtiRankings.topCountries.map((c, idx) => (
                    <tr
                      key={c.country_code}
                      className="border-t border-slate-100 dark:border-outline-variant/10 hover:bg-slate-50 dark:hover:bg-surface-container-low/80"
                    >
                      <td className="px-3 py-2 font-medium text-slate-700 dark:text-on-surface-variant">{idx + 1}</td>
                      <td className="px-3 py-2 font-semibold text-slate-900 dark:text-on-surface">{c.country_code}</td>
                      <td className="px-3 py-2 text-right">
                        <span
                          className={`font-bold ${
                            parseFloat(c.gti_score) >= 50
                              ? 'text-emerald-600 dark:text-tertiary'
                              : 'text-red-600 dark:text-error'
                          }`}
                        >
                          {parseFloat(c.gti_score).toFixed(1)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-slate-600 dark:text-on-surface-variant">{c.event_count}</td>
                      <td className="px-3 py-2 text-center">
                        {c.trend === 'improving' ? (
                          <span className="inline-flex items-center text-emerald-600 dark:text-tertiary">
                            <TrendingUp className="h-4 w-4" />
                          </span>
                        ) : c.trend === 'declining' ? (
                          <span className="inline-flex items-center text-red-600 dark:text-error">
                            <TrendingDown className="h-4 w-4" />
                          </span>
                        ) : (
                          <span className="text-slate-400 dark:text-outline">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* ── Sector Benchmarks + Legacy Tariff Chart ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {sectorData.length > 0 ? (
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 transition-colors dark:border-outline-variant/15 dark:bg-surface-container">
            <h2 className="font-headline text-xl font-semibold text-slate-900 dark:text-on-surface mb-4">📦 Sector GTI Benchmarks</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sectorData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="score" name="Avg GTI" fill="#4edea3" />
                <Bar dataKey="events" name="Events" fill="#b4c5ff" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : tariffChartData.length > 0 ? (
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 transition-colors dark:border-outline-variant/15 dark:bg-surface-container">
            <h2 className="font-headline text-xl font-semibold text-slate-900 dark:text-on-surface mb-4">🌐 Top Countries by GTI Score</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={tariffChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="GTI Score" fill="#2563eb" />
                <Bar dataKey="Events" fill="#b4c5ff" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 transition-colors dark:border-outline-variant/15 dark:bg-surface-container">
            <div className="flex h-[300px] items-center justify-center text-slate-400 dark:text-on-surface-variant">
              <div className="text-center">
                <Activity className="mx-auto mb-3 h-12 w-12 opacity-50" />
                <p>Run the pipeline to compute GTI scores</p>
                <p className="mt-1 text-sm">npm run pipeline</p>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 transition-colors dark:border-outline-variant/15 dark:bg-surface-container">
          <h2 className="font-headline text-xl font-semibold text-slate-900 dark:text-on-surface mb-4">🔍 Trade Event Breakdown</h2>
          {intel.tradeEvents > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                { name: 'Tariff Changes', value: intel.tariffEvents || 0 },
                { name: 'Trade Deals', value: intel.dealEvents || 0 },
                { name: 'Sanctions', value: intel.sanctionEvents || 0 },
                { name: 'Bans & Quotas', value: intel.restrictionEvents || 0 },
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" name="Events" fill="#b4c5ff">
                  <Cell fill="#2563eb" />
                  <Cell fill="#4edea3" />
                  <Cell fill="#ffb4ab" />
                  <Cell fill="#b9c7df" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[300px] items-center justify-center text-slate-400 dark:text-on-surface-variant">
              <div className="text-center">
                <Activity className="mx-auto mb-3 h-12 w-12 opacity-50" />
                <p>Run the pipeline to populate trade events</p>
                <p className="mt-1 text-sm">npm run pipeline</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Recent Legislation ── */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-headline text-2xl font-bold text-slate-900 dark:text-on-surface">Recent Legislation</h2>
          <Link
            to="/legislation"
            className="flex items-center font-medium text-primary-600 hover:text-primary-700 dark:text-primary dark:hover:text-primary-fixed-dim"
          >
            View All
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
        {recentLegislation.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recentLegislation.slice(0, 6).map((legislation) => (
              <LegislationCard key={legislation.id} legislation={legislation} />
            ))}
          </div>
        ) : (
          <p className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 text-slate-500 dark:border-outline-variant/15 dark:bg-surface-container dark:text-on-surface-variant">
            No recent legislation found.
          </p>
        )}
      </div>

      {/* ── Research & methodology (internal links for SEO / E-E-A-T) ── */}
      <div className="mb-8 rounded-stitch-xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white p-6 dark:border-outline-variant/15 dark:from-surface-container-low dark:to-surface-container">
        <h2 className="mb-2 font-headline text-xl font-bold text-slate-900 dark:text-on-surface">Research & methodology</h2>
        <p className="mb-4 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-on-surface-variant">
          First-party guides explain how TradeRadar scores trade friction, how Hormuz risk propagates to
          freight and prices, and how to use tariffs data alongside legislation. Written for analysts—not
          keyword pages.
        </p>
        <div className="flex flex-wrap gap-3 text-sm">
          <Link
            to="/insights"
            className="inline-flex items-center font-medium text-primary-700 hover:underline dark:text-primary"
          >
            Insights hub
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
          <Link
            to="/insights/methodology"
            className="inline-flex items-center font-medium text-primary-700 hover:underline dark:text-primary"
          >
            GTI methodology
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
          <Link
            to="/editorial-standards"
            className="inline-flex items-center font-medium text-primary-700 hover:underline dark:text-primary"
          >
            Editorial standards
            <ArrowRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      </div>

      {/* ── Quick Links ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/countries"
          className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 transition-shadow hover:shadow-intel-glow dark:border-outline-variant/15 dark:bg-surface-container dark:hover:border-primary/20"
        >
          <Globe className="mb-3 h-8 w-8 text-primary-600 dark:text-primary" />
          <h3 className="mb-2 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">Explore Countries</h3>
          <p className="text-slate-600 dark:text-on-surface-variant">View detailed country profiles and trade statistics</p>
        </Link>
        <Link
          to="/tariffs"
          className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 transition-shadow hover:shadow-intel-glow dark:border-outline-variant/15 dark:bg-surface-container dark:hover:border-primary/20"
        >
          <TrendingUp className="mb-3 h-8 w-8 text-primary-600 dark:text-primary" />
          <h3 className="mb-2 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">Analyze Tariffs</h3>
          <p className="text-slate-600 dark:text-on-surface-variant">Compare tariff rates across countries and products</p>
        </Link>
        <Link
          to="/compare"
          className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 transition-shadow hover:shadow-intel-glow dark:border-outline-variant/15 dark:bg-surface-container dark:hover:border-primary/20"
        >
          <Activity className="mb-3 h-8 w-8 text-primary-600 dark:text-primary" />
          <h3 className="mb-2 font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">Compare GTI</h3>
          <p className="text-slate-600 dark:text-on-surface-variant">Side-by-side comparison using the Global Trade Index</p>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
